import { useEffect, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { Smile, ThumbsUp, ThumbsDown, RefreshCw, Trophy } from "lucide-react";
import { V } from "@/lib/vocab";

function scoreColor(pct) {
  if (pct == null) return "text-muted-foreground";
  if (pct >= 80) return "text-emerald-600";
  if (pct >= 50) return "text-amber-600";
  return "text-redline-600";
}

export default function Satisfaction() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/admin/csat");
    setData(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="satisfaction-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <PageTitle title={<><Smile className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> {V.one("person")} Satisfaction</>} about={<>One-tap 👍/👎 ratings collected after conversations resolve, across every {V.lc("org")}.</>} />
        </div>
        <button data-testid="csat-refresh" onClick={load} className="h-9 w-9 rounded-full bg-white border border-sand-200 flex items-center justify-center hover:border-ink transition-colors">
          <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </div>

      {/* Global summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-sand-200 p-5">
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><Trophy className="h-4 w-4" /> Overall satisfaction</div>
          <div data-testid="csat-global-score" className={`font-heading text-4xl font-extrabold mt-2 ${scoreColor(data.score)}`}>
            {data.score != null ? `${data.score}%` : "—"}
          </div>
          <div className="text-xs text-muted-foreground mt-1">across all {V.lcs("org")} · {data.total} rating{data.total === 1 ? "" : "s"}</div>
        </div>
        <div className="bg-white rounded-2xl border border-sand-200 p-5">
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><ThumbsUp className="h-4 w-4" /> Positive</div>
          <div className="font-heading text-4xl font-extrabold mt-2 text-emerald-600">{data.total_up}</div>
          <div className="text-xs text-muted-foreground mt-1">{V.lcs("person")} said 👍</div>
        </div>
        <div className="bg-white rounded-2xl border border-sand-200 p-5">
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><ThumbsDown className="h-4 w-4" /> Negative</div>
          <div className="font-heading text-4xl font-extrabold mt-2 text-redline-600">{data.total_down}</div>
          <div className="text-xs text-muted-foreground mt-1">{V.lcs("person")} said 👎</div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-sand-200"><h2 className="font-heading font-bold text-lg">By {V.lc("org")}</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-sand-200">
                <th className="px-5 py-3 font-semibold">#</th>
                <th className="px-3 py-3 font-semibold">{V.one("org")}</th>
                <th className="px-3 py-3 font-semibold text-right">Score</th>
                <th className="px-3 py-3 font-semibold text-right">👍</th>
                <th className="px-3 py-3 font-semibold text-right">👎</th>
                <th className="px-3 py-3 font-semibold min-w-[140px]">Positive share</th>
                <th className="px-5 py-3 font-semibold text-right">Awaiting</th>
              </tr>
            </thead>
            <tbody>
              {data.communities.map((c, i) => (
                <tr key={c.tenant_id} data-testid={`csat-row-${c.tenant_id}`} className="border-b border-sand-100 last:border-0 hover:bg-sand-50">
                  <td className="px-5 py-3 text-muted-foreground font-semibold">{c.total > 0 ? i + 1 : "—"}</td>
                  <td className="px-3 py-3 font-semibold text-ink">
                    {c.name}
                    {!c.enabled && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide bg-sand-100 text-muted-foreground rounded-full px-2 py-0.5">CSAT off</span>}
                  </td>
                  <td className={`px-3 py-3 text-right font-heading font-extrabold text-lg ${scoreColor(c.score)}`}>{c.score != null ? `${c.score}%` : "—"}</td>
                  <td className="px-3 py-3 text-right text-emerald-600 font-semibold">{c.up}</td>
                  <td className="px-3 py-3 text-right text-danger-600 font-semibold">{c.down}</td>
                  <td className="px-3 py-3">
                    {c.total > 0 ? (
                      <div className="h-2 rounded-full bg-sand-100 overflow-hidden flex w-32">
                        <div className="h-full bg-emerald-500" style={{ width: `${c.score}%` }} />
                        <div className="h-full bg-redline-500" style={{ width: `${100 - c.score}%` }} />
                      </div>
                    ) : <span className="text-xs text-muted-foreground">No ratings yet</span>}
                  </td>
                  <td className="px-5 py-3 text-right text-muted-foreground">{c.pending || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
